import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { User, BookOpen, MapPin, Bookmark, Eye, LogOut, Settings, Crown, Calendar, Sparkles, Coins, TrendingUp, CreditCard, CheckCircle2, XCircle, Clock, GraduationCap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { usePreferences } from '../context/PreferencesContext';
import { toast } from 'sonner';
import AuthModal from '../components/auth/AuthModal';
import UserDetailsSection from '../components/profile/UserDetailsSection';
import ChangePasswordSection from '../components/profile/ChangePasswordSection';
import SecurityQuestionsSection from '../components/profile/SecurityQuestionsSection';
import DeleteAccountSection from '../components/profile/DeleteAccountSection';
import NotificationPreferences from '../components/NotificationPreferences';
import ActiveSessionsSection from '../components/profile/ActiveSessionsSection';
import { TwoFactorSetupModal, TwoFactorStatusCard } from '../components/auth/TwoFactorAuth';
import { useMMDLocationsDetailed } from '../hooks/useMMDLocations';
import { useModuleAccess } from '../hooks/useModuleAccess';
import UpgradeOptionsModal from '../components/common/UpgradeOptionsModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALL_GRADES = ['MEO Class 1', 'MEO Class 2', 'MEO Class 4', 'ETO', 'Master', 'Chief Mate', '2MFG'];

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, loading: authLoading } = useAuth();
  const { preferences, updatePreferences } = usePreferences();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [profileDataLoading, setProfileDataLoading] = useState(true);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Fetch MMD locations with grade info from API
  const { locations: mmdLocationsDetailed, loading: mmdLoading, getGradesForMMD, getMMDsForGrade } = useMMDLocationsDetailed();
  
  // Check module statuses for filtering display
  const { 
    isFeatureActive, 
    getModuleStatus, 
    isWrittenActive,
    isModuleHidden,
    getNotebookStatusForGrade,
    getInsightsStatusForGrade,
    getPracticeSessionsStatus,
    getProgressTrackerStatus,
    getLiveClassesStatusForGrade
  } = useModuleAccess();
  
  const coinRewardsActive = isFeatureActive('coin_rewards');
  const coinRewardsStatus = getModuleStatus('coin_rewards');
  
  // Check if written questions are active for user's grade
  const writtenModuleActive = preferences?.grade ? isWrittenActive(preferences.grade) : false;
  
  // Get module statuses for plan display filtering
  const userGrade = preferences?.grade;
  const moduleStatuses = {
    practice_sessions: getPracticeSessionsStatus(),
    progress_tracker: getProgressTrackerStatus(),
    notebook: userGrade ? getNotebookStatusForGrade(userGrade) : 'active',
    exam_insights: userGrade ? getInsightsStatusForGrade(userGrade) : 'active',
    // Live classes shipped after the original plan card was designed —
    // add it here so admin (module_access_override) and paid users on
    // live-classes plans see it listed in their plan-modules matrix.
    live_classes: userGrade ? getLiveClassesStatusForGrade(userGrade) : 'active'
  };
  
  // Module display config - filter out hidden, mark coming soon
  const getVisibleModules = () => {
    const modules = [
      { key: 'practice_sessions', label: 'Practice Sessions' },
      { key: 'progress_tracker', label: 'Progress Tracker' },
      { key: 'notebook', label: 'Study Notebook' },
      { key: 'exam_insights', label: 'Exam Insights' },
      { key: 'live_classes', label: 'Live Classes' }
    ];
    
    return modules.filter(m => moduleStatuses[m.key] !== 'hidden')
      .map(m => ({
        ...m,
        isComingSoon: moduleStatuses[m.key] === 'coming_soon'
      }));
  };
  
  const visibleModules = getVisibleModules();
  
  const [tempPrefs, setTempPrefs] = useState({
    grade: preferences.grade || '',
    mmd_location: preferences.mmd_location || ''
  });

  // Get available grades based on selected MMD (must be after tempPrefs declaration)
  const availableGrades = tempPrefs.mmd_location 
    ? getGradesForMMD(tempPrefs.mmd_location)
    : ALL_GRADES;

  // Get available MMDs based on selected grade
  const availableMMDs = getMMDsForGrade(tempPrefs.grade);

  // Coins data derived from user object (no extra API call needed).
  // `questions_contributed` is intentionally sourced from `stats` (which
  // counts actual `question_approved`/`question_merged_duplicate` earn
  // events), NOT from `coins_earned_total` — that total also includes
  // admin manual credits and would misattribute reward coins as
  // "Questions Contributed".
  const coinsData = user ? {
    coins_balance: user.coins_balance || 0,
    coins_earned_total: user.coins_earned_total || 0,
    questions_contributed: stats?.questions_contributed || 0
  } : null;

  // Fetch all profile data in parallel
  const fetchProfileData = async () => {
    setProfileDataLoading(true);
    try {
      const [statsRes, subscriptionRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/progress/stats`).catch(err => ({ data: null, error: err })),
        axios.get(`${API}/user/subscription`).catch(err => ({ data: null, error: err })),
        axios.get(`${API}/user/transactions?per_page=5`).catch(err => ({ data: { items: [] }, error: err }))
      ]);
      
      if (statsRes.data && !statsRes.error) setStats(statsRes.data);
      if (subscriptionRes.data && !subscriptionRes.error) setSubscriptionStatus(subscriptionRes.data);
      if (transactionsRes.data && !transactionsRes.error) setTransactions(transactionsRes.data.items || []);
    } catch (error) {
      console.error('Failed to fetch profile data:', error);
    } finally {
      setProfileDataLoading(false);
    }
  };

  useEffect(() => {
    // Wait for auth to finish loading before deciding
    if (authLoading) return;
    
    if (isAuthenticated()) {
      fetchProfileData();
    } else {
      setProfileDataLoading(false);
    }
  }, [authLoading, refreshKey]);

  // Reset grade when MMD changes and current grade is not supported
  // Only validate after MMD data is loaded to prevent false positives
  useEffect(() => {
    if (mmdLoading || mmdLocationsDetailed.length === 0) return; // Wait for data to load
    
    if (tempPrefs.mmd_location && tempPrefs.grade) {
      const supportedGrades = getGradesForMMD(tempPrefs.mmd_location);
      if (!supportedGrades.includes(tempPrefs.grade)) {
        setTempPrefs(prev => ({ ...prev, grade: '' }));
        toast.info(`Grade "${tempPrefs.grade}" is not available at ${tempPrefs.mmd_location}. Please select another grade.`);
      }
    }
  }, [tempPrefs.mmd_location, tempPrefs.grade, getGradesForMMD, mmdLoading, mmdLocationsDetailed.length]);

  // Reset MMD when grade changes and current MMD doesn't support the new grade
  // Only validate after MMD data is loaded to prevent false positives
  useEffect(() => {
    if (mmdLoading || mmdLocationsDetailed.length === 0) return; // Wait for data to load
    
    if (tempPrefs.grade && tempPrefs.mmd_location) {
      const supportedMMDs = getMMDsForGrade(tempPrefs.grade);
      if (!supportedMMDs.includes(tempPrefs.mmd_location)) {
        setTempPrefs(prev => ({ ...prev, mmd_location: '' }));
        toast.info(`${tempPrefs.mmd_location} doesn't support ${tempPrefs.grade}. Please select another MMD.`);
      }
    }
  }, [tempPrefs.grade, tempPrefs.mmd_location, getMMDsForGrade, mmdLoading, mmdLocationsDetailed.length]);

  const handleSavePreferences = async () => {
    setLoading(true);
    await updatePreferences(tempPrefs);
    setEditMode(false);
    setLoading(false);
    toast.success('Preferences updated successfully');
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleUserUpdate = () => {
    setRefreshKey(prev => prev + 1);
    window.location.reload();
  };

  if (!isAuthenticated()) {
    return (
      <Layout>
        <div className="page-transition">
          <Card className="max-w-2xl mx-auto text-center py-12">
            <CardContent>
              <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Login Required</h2>
              <p className="text-gray-600 mb-6">
                Please login to view your profile
              </p>
              <Button
                data-testid="profile-login-btn"
                onClick={() => setShowAuth(true)}
                className="bg-[#0A7EA4] hover:bg-[#0A7EA4]/90"
              >
                Login / Register
              </Button>
            </CardContent>
          </Card>
          <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-transition max-w-5xl mx-auto space-y-6">
        {/* Profile Header Card */}
        <Card className="shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#0A7EA4]/5 to-[#14B8A6]/5">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-3xl text-[#0A7EA4] flex items-center gap-3">
                  <User className="w-8 h-8" />
                  {user?.name}
                </CardTitle>
                <p className="text-gray-600 mt-2">{user?.email}</p>
                {user?.role && (
                  <Badge className="mt-2 bg-[#0A7EA4] text-white">
                    {user.role === 'sub_admin' ? 'Sub Admin' : user.role}
                  </Badge>
                )}
              </div>
              <Button
                data-testid="logout-btn"
                onClick={handleLogout}
                variant="outline"
                className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </CardHeader>

          <CardContent className="p-8">
            {/* Stats - Split by Oral and Written */}
            {stats && (
              <div className="space-y-6 mb-8">
                {/* Oral Questions Stats */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#0A7EA4]"></span>
                    Oral Questions
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-[#0A7EA4]/5 rounded-lg">
                      <Eye className="w-6 h-6 text-[#0A7EA4] mx-auto mb-1" />
                      <div className="text-2xl font-bold text-[#0A7EA4]">{stats.oral?.viewed || 0}</div>
                      <div className="text-xs text-gray-600">Viewed</div>
                    </div>
                    <div className="text-center p-4 bg-[#14B8A6]/5 rounded-lg">
                      <Bookmark className="w-6 h-6 text-[#14B8A6] mx-auto mb-1" />
                      <div className="text-2xl font-bold text-[#14B8A6]">{stats.oral?.bookmarked || 0}</div>
                      <div className="text-xs text-gray-600">Bookmarked</div>
                    </div>
                    <div className="text-center p-4 bg-[#FF6B6B]/5 rounded-lg">
                      <BookOpen className="w-6 h-6 text-[#FF6B6B] mx-auto mb-1" />
                      <div className="text-2xl font-bold text-[#FF6B6B]">{Math.round(stats.oral?.progress_percentage || 0)}%</div>
                      <div className="text-xs text-gray-600">Progress</div>
                    </div>
                  </div>
                </div>

                {/* Written Questions Stats - Only show if written module is active */}
                {writtenModuleActive && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                      Written Questions
                    </h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <Eye className="w-6 h-6 text-purple-600 mx-auto mb-1" />
                        <div className="text-2xl font-bold text-purple-600">{stats.written?.viewed || 0}</div>
                        <div className="text-xs text-gray-600">Viewed</div>
                      </div>
                      <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <Bookmark className="w-6 h-6 text-purple-600 mx-auto mb-1" />
                        <div className="text-2xl font-bold text-purple-600">{stats.written?.bookmarked || 0}</div>
                        <div className="text-xs text-gray-600">Bookmarked</div>
                      </div>
                      <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <BookOpen className="w-6 h-6 text-purple-600 mx-auto mb-1" />
                        <div className="text-2xl font-bold text-purple-600">{Math.round(stats.written?.progress_percentage || 0)}%</div>
                        <div className="text-xs text-gray-600">Progress</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Coins & Rewards Section */}
            {coinsData && coinRewardsActive && (
              <>
                <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg p-6 border-2 border-amber-200 mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold flex items-center gap-2 text-amber-900">
                      <Coins className="w-6 h-6 text-amber-600" />
                      Reward Coins
                    </h3>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-amber-600">{coinsData.coins_balance}</div>
                      <div className="text-sm text-gray-600">Available</div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/60 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                        <TrendingUp className="w-4 h-4" />
                        Total Earned
                      </div>
                      <div className="text-2xl font-bold text-amber-700">{coinsData.coins_earned_total}</div>
                    </div>
                    <div className="bg-white/60 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                        <BookOpen className="w-4 h-4" />
                        Questions Contributed
                      </div>
                      <div className="text-2xl font-bold text-amber-700">{coinsData.questions_contributed}</div>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-3 bg-white/80 rounded-lg">
                    <p className="text-sm text-gray-700">
                      💡 <strong>Earn coins:</strong> Submit questions after your exam. Get 1 coin for each approved question!
                    </p>
                    <p className="text-sm text-gray-700 mt-1">
                      💰 <strong>Use coins:</strong> Redeem during checkout to get discounts on premium plans.
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* Coins & Rewards - Coming Soon */}
            {coinsData && !coinRewardsActive && coinRewardsStatus === 'coming_soon' && (
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-lg p-6 border-2 border-gray-200 mb-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                      <Coins className="w-6 h-6 text-amber-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-700">Reward Coins</h3>
                      <p className="text-sm text-gray-500">Earn coins by contributing questions</p>
                    </div>
                  </div>
                  <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-300">
                    Coming Soon
                  </Badge>
                </div>
                <p className="mt-4 text-sm text-gray-600">
                  🚀 We&apos;re working on an exciting reward system! Soon you&apos;ll be able to earn coins for contributing questions and redeem them for discounts.
                </p>
              </div>
            )}

            <Separator className="my-6" />

            {/* Subscription Plan */}
            <div>
              <h3 className="text-xl font-semibold flex items-center gap-2 mb-4">
                <Crown className="w-5 h-5 text-yellow-600" />
                Your Plan
              </h3>
              
              {subscriptionStatus ? (
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {/* Admin/sub-admin get a distinct "Admin" pill —
                            matches the "Admin Access" label from the API
                            fallback for staff accounts. */}
                        {(subscriptionStatus.user_role === 'admin' || subscriptionStatus.user_role === 'sub_admin') ? (
                          <Badge className="bg-[#0A7EA4] hover:bg-[#0A7EA4]">Admin</Badge>
                        ) : (
                          <Badge className={subscriptionStatus.subscription_status === 'paid' ? 'bg-green-600' : 'bg-gray-600'}>
                            {subscriptionStatus.subscription_status === 'paid' ? 'Premium' : 'Free'}
                          </Badge>
                        )}
                        {subscriptionStatus.is_active && (
                          <Badge variant="outline" className="text-green-600 border-green-600">
                            Active
                          </Badge>
                        )}
                        {subscriptionStatus.subscribed_grade && subscriptionStatus.subscription_status === 'paid' && (
                          <Badge variant="outline" className="text-purple-600 border-purple-600">
                            {subscriptionStatus.subscribed_grade === 'all' ? 'All Grades' : subscriptionStatus.subscribed_grade}
                          </Badge>
                        )}
                      </div>
                      <p className="text-lg font-semibold text-gray-800 mb-1">
                        {subscriptionStatus.plan_name}
                      </p>
                      
                      {/* Plan Description */}
                      {subscriptionStatus.plan_details?.description && (
                        <p className="text-sm text-gray-600 mb-2">
                          {subscriptionStatus.plan_details.description}
                        </p>
                      )}
                      
                      {subscriptionStatus.subscription_expires_at && (
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {subscriptionStatus.is_active ? 'Valid until' : 'Expired on'}{' '}
                            {new Date(subscriptionStatus.subscription_expires_at).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                      )}

                      {/* Subscribed Grade — the grade this plan unlocks. Deliberately
                          rendered as a distinct labeled row (not just a pill) so users
                          don't confuse it with their viewing preference in Preferences.
                          Both fields answer different questions:
                             preference.grade    = "what am I studying right now?"
                             subscribed_grade    = "what did my plan pay for?"           */}
                      {subscriptionStatus.subscription_status === 'paid' && subscriptionStatus.subscribed_grade && (
                        <div
                          className="flex items-center gap-2 text-sm text-gray-700 mb-3"
                          data-testid="profile-subscribed-grade"
                        >
                          <GraduationCap className="w-4 h-4 text-purple-600" />
                          <span>
                            <span className="text-gray-500">Subscribed Grade:</span>{' '}
                            <span className="font-semibold text-gray-900">
                              {subscriptionStatus.subscribed_grade === 'all'
                                ? 'All Grades'
                                : subscriptionStatus.subscribed_grade}
                            </span>
                          </span>
                          <span className="text-[11px] text-gray-500 italic">
                            (set by your plan &mdash; separate from your viewing preference)
                          </span>
                        </div>
                      )}
                      
                      {/* Module Access */}
                      {subscriptionStatus.subscription_status === 'paid' && subscriptionStatus.module_access && (
                        <div className="mt-3 pt-3 border-t border-blue-200">
                          <p className="text-xs font-semibold text-gray-500 mb-2">Module Access:</p>
                          
                          {/* Available Now */}
                          {visibleModules.some(m => !m.isComingSoon) && (
                            <div className="mb-2">
                              <p className="text-[10px] font-medium text-green-600 mb-1 flex items-center gap-1">
                                <CheckCircle2 className="w-2.5 h-2.5" />
                                Available Now
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {visibleModules.filter(m => !m.isComingSoon).map(({ key, label }) => {
                                  const hasAccess = subscriptionStatus.module_access[key];
                                  return (
                                    <Badge 
                                      key={key}
                                      variant="outline" 
                                      className={`text-xs ${hasAccess ? 'bg-green-50 text-green-700 border-green-300' : 'bg-gray-100 text-gray-400 border-gray-300'}`}
                                    >
                                      {hasAccess ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                                      {label}
                                    </Badge>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          {/* Coming Soon */}
                          {visibleModules.some(m => m.isComingSoon) && (
                            <div>
                              <p className="text-[10px] font-medium text-amber-600 mb-1 flex items-center gap-1">
                                <Clock className="w-2.5 h-2.5" />
                                Coming Soon
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {visibleModules.filter(m => m.isComingSoon).map(({ key, label }) => (
                                  <Badge 
                                    key={key}
                                    variant="outline" 
                                    className="text-xs bg-amber-50 text-amber-700 border-amber-300"
                                  >
                                    <Clock className="w-3 h-3 mr-1" />
                                    {label}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {subscriptionStatus.module_access_override && (
                            <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                              <Sparkles className="w-3 h-3" />
                              Full access granted (Admin Override)
                            </p>
                          )}
                          
                          {/* Upgrade Button - Show only if there are locked modules that are active (not hidden/coming soon) */}
                          {!subscriptionStatus.module_access_override && 
                           visibleModules.some(m => !m.isComingSoon && !subscriptionStatus.module_access[m.key]) && (
                            <Button
                              onClick={() => setShowUpgradeModal(true)}
                              className="mt-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
                              size="sm"
                              data-testid="profile-upgrade-button"
                            >
                              <TrendingUp className="w-4 h-4 mr-2" />
                              Upgrade to Unlock More Features
                            </Button>
                          )}
                        </div>
                      )}
                      
                      {/* Plan Features */}
                      {subscriptionStatus.plan_details?.features?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-blue-200">
                          <p className="text-xs font-semibold text-gray-500 mb-2">Plan Features:</p>
                          <ul className="text-sm text-gray-600 space-y-1">
                            {subscriptionStatus.plan_details.features.slice(0, 4).map((feature, idx) => (
                              <li key={idx} className="flex items-center gap-2">
                                <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" />
                                {feature}
                              </li>
                            ))}
                            {subscriptionStatus.plan_details.features.length > 4 && (
                              <li className="text-xs text-gray-500">
                                +{subscriptionStatus.plan_details.features.length - 4} more features
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                      
                      {subscriptionStatus.subscription_status === 'free' && (
                        <p className="text-sm text-gray-600 mt-2">
                          Upgrade to premium to unlock all answers, practice tests, and progress tracking
                        </p>
                      )}
                    </div>
                    <Button
                      onClick={() => navigate('/plans')}
                      className="w-full md:w-auto md:ml-4 flex-shrink-0"
                    >
                      {subscriptionStatus.subscription_status === 'paid' && subscriptionStatus.is_active ? (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Extend Plan
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Upgrade Now
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : profileDataLoading ? (
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-gray-300 rounded-full animate-spin" style={{ borderTopColor: '#0A7EA4' }} />
                    <p className="text-gray-600">Loading subscription details...</p>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 text-center">
                  <p className="text-gray-600">Unable to load subscription details</p>
                </div>
              )}
            </div>

            <Separator className="my-6" />

            {/* Preferences */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Exam Preferences
                </h3>
                {!editMode ? (
                  <Button
                    data-testid="edit-preferences-btn"
                    onClick={() => setEditMode(true)}
                    variant="outline"
                    size="sm"
                  >
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      data-testid="cancel-edit-btn"
                      onClick={() => {
                        setEditMode(false);
                        setTempPrefs({
                          grade: preferences.grade || '',
                          mmd_location: preferences.mmd_location || ''
                        });
                      }}
                      variant="outline"
                      size="sm"
                    >
                      Cancel
                    </Button>
                    <Button
                      data-testid="save-preferences-btn"
                      onClick={handleSavePreferences}
                      size="sm"
                      className="bg-[#0A7EA4] hover:bg-[#0A7EA4]/90"
                      disabled={loading}
                    >
                      Save
                    </Button>
                  </div>
                )}
              </div>

              {!editMode ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-600">Grade:</span>
                    <Badge className="bg-[#0A7EA4] text-white">
                      {preferences.grade || 'Not set'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-600">MMD Location:</span>
                    <Badge className="bg-[#14B8A6] text-white">
                      {preferences.mmd_location || 'Not set'}
                    </Badge>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="grade">Grade</Label>
                    <Select
                      value={tempPrefs.grade}
                      onValueChange={(value) => setTempPrefs({ ...tempPrefs, grade: value })}
                    >
                      <SelectTrigger data-testid="profile-grade-select" className="mt-1">
                        <SelectValue placeholder="Select grade" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableGrades.map((grade) => (
                          <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {tempPrefs.mmd_location && availableGrades.length < ALL_GRADES.length && (
                      <p className="text-xs text-amber-600 mt-1">
                        Showing grades available at {tempPrefs.mmd_location}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="mmd_location">MMD Location</Label>
                    <Select
                      value={tempPrefs.mmd_location}
                      onValueChange={(value) => setTempPrefs({ ...tempPrefs, mmd_location: value })}
                    >
                      <SelectTrigger data-testid="profile-mmd-select" className="mt-1">
                        <SelectValue placeholder="Select MMD location" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableMMDs.map((location) => (
                          <SelectItem key={location} value={location}>{location}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {tempPrefs.grade && availableMMDs.length < mmdLocationsDetailed.length && (
                      <p className="text-xs text-amber-600 mt-1">
                        Showing MMDs available for {tempPrefs.grade}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* User Details Section */}
        <UserDetailsSection user={user} onUpdate={handleUserUpdate} />

        {/* Change Password Section */}
        <ChangePasswordSection />

        {/* Transaction History Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Transaction History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {profileDataLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A7EA4]"></div>
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No transactions yet</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => navigate('/plans')}
                >
                  View Plans
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((txn) => (
                      <TableRow key={txn.id}>
                        <TableCell className="text-sm">
                          {new Date(txn.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{txn.plan_name || txn.plan_type || 'N/A'}</span>
                        </TableCell>
                        <TableCell className="font-semibold">
                          ₹{txn.amount ? (txn.amount / 100).toFixed(2) : '0.00'}
                        </TableCell>
                        <TableCell>
                          {txn.status === 'paid' ? (
                            <Badge className="bg-green-600 text-white">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Paid
                            </Badge>
                          ) : txn.status === 'failed' ? (
                            <Badge className="bg-red-600 text-white">
                              <XCircle className="w-3 h-3 mr-1" />
                              Failed
                            </Badge>
                          ) : txn.status === 'cancelled' ? (
                            <Badge className="bg-orange-500 text-white">
                              <XCircle className="w-3 h-3 mr-1" />
                              Cancelled
                            </Badge>
                          ) : txn.status === 'expired' ? (
                            <Badge className="bg-gray-500 text-white">
                              <Clock className="w-3 h-3 mr-1" />
                              Expired
                            </Badge>
                          ) : txn.status === 'created' ? (
                            <Badge variant="outline" className="text-blue-600 border-blue-600">
                              <Clock className="w-3 h-3 mr-1" />
                              Awaiting
                            </Badge>
                          ) : (
                            <Badge variant="outline">
                              <Clock className="w-3 h-3 mr-1" />
                              {txn.status || 'Unknown'}
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Security Questions Section */}
        <SecurityQuestionsSection />

        {/* Active Sessions Section */}
        <ActiveSessionsSection onLogout={logout} />

        {/* Two-Factor Authentication Section (visibility controlled by module status) */}
        <TwoFactorStatusCard 
          onSetupClick={() => setShow2FASetup(true)} 
        />

        {/* Notification Preferences Section */}
        <NotificationPreferences />

        {/* Delete Account Section - At the bottom */}
        <DeleteAccountSection />
      </div>

      {/* 2FA Setup Modal */}
      <TwoFactorSetupModal
        isOpen={show2FASetup}
        onClose={() => setShow2FASetup(false)}
        onSuccess={() => setShow2FASetup(false)}
      />

      {/* Upgrade Options Modal */}
      <UpgradeOptionsModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </Layout>
  );
};

export default ProfilePage;
